import type { PurposeAreaStyle } from "@/purpose-sections/model";

/** Optional palette overrides; omitted or empty values inherit the website theme. */
export interface BlogThemeSurface {
  bgColor?: string;
  textColor?: string;
  headingColor?: string;
}

export interface BlogTheme extends BlogThemeSurface {
  mutedTextColor?: string;
  borderColor?: string;
  card?: BlogThemeSurface;
  featured?: BlogThemeSurface;
  overlay?: BlogThemeSurface;
  callout?: BlogThemeSurface;
  author?: BlogThemeSurface;
  cta?: BlogThemeSurface;
  newsletter?: BlogThemeSurface;
  state?: BlogThemeSurface;
}

/** Blog content is independent of CMS pages; sections choose its presentation. */
export interface ArticleImage {
  url: string;
  altText?: string;
  width?: number;
  height?: number;
  caption?: string;
  position?: string;
}

/** Shared typography controls, limited to the properties supported by article rows. */
export type ArticleBlockTypography = Pick<
  PurposeAreaStyle,
  "fontSize" | "fontWeight" | "lineHeight" | "letterSpacing" | "margin"
> & { fontRole?: "body" | "heading" };

export interface ArticleImageTextBlock {
  id: string;
  type: "imageText";
  html: string;
  image: ArticleImage;
  imageSide?: "left" | "right";
  /** Decimal percentage from 10% to 90%; defaults to 50%. */
  imageWidth?: string;
  gap?: string;
  /** Signed spacing/CSS box, optionally `mobile | desktop`. */
  margin?: string;
  contentPadding?: string;
  mediaPadding?: string;
  /** `auto` or a non-negative length, optionally `mobile | desktop`. */
  imageHeight?: string;
  stackBreakpoint?: 768 | 992;
  mobileOrder?: "image-first" | "text-first" | "follow-side";
  styles?: {
    text?: ArticleBlockTypography;
    heading?: ArticleBlockTypography;
    subheading?: ArticleBlockTypography;
  };
}

export type ArticleBlock =
  | { id: string; type: "richText"; html: string }
  | { id: string; type: "image"; image: ArticleImage }
  | ArticleImageTextBlock
  | { id: string; type: "gallery"; images: ArticleImage[] }
  | {
      id: string;
      type: "video";
      url: string;
      poster?: ArticleImage;
      caption?: string;
    }
  | { id: string; type: "quote"; text: string; attribution?: string }
  | { id: string; type: "callout"; title: string; text: string }
  | { id: string; type: "button"; label: string; url: string };

export interface BlogAuthor {
  id: string;
  name: string;
  /** The author page's address segment; derived from the name when absent. */
  slug?: string;
  title?: string;
  /** The employee the author was made from; only the CMS reads it. */
  employeeId?: string;
  bio?: string;
  image?: ArticleImage;
  url?: string;
  email?: string;
  phone?: string;
}

/** An article topic. The order of `BlogData.topics` is the order on the website. */
export interface BlogTopic {
  id: string;
  name: string;
  slug: string;
  /** Earlier slugs; their topic pages redirect permanently to the current one. */
  previousSlugs?: string[];
  description?: string;
}

/**
 * An artikelside. One with a `path` serves its articles at `{path}/{slug}` through the `type: "article"` page
 * `templatePageId`; one without keeps the legacy `{detailsPageSlug}/articles/{slug}` addresses.
 */
export interface Blog {
  id: string;
  title: string;
  path?: string;
  default?: boolean;
  templatePageId?: string;
  /** `{path}/emne/{topic}` pages; on unless explicitly false. */
  topicPages?: boolean;
  /** `{path}/forfatter/{author}` pages; off unless explicitly true. */
  authorPages?: boolean;
  /** Legacy destination; ignored when `path` is set. */
  detailsPageSlug?: string;
}

export interface Article {
  id: string;
  blogId: string;
  /** Unique within its artikelside; legacy addresses do not contain a blog, so the CMS keeps it unique site-wide. */
  slug: string;
  previousSlugs?: string[];
  /** Full former addresses from before a move to another artikelside; they redirect to the current one. */
  previousPaths?: string[];
  title: string;
  excerpt: string;
  status: "draft" | "published" | "archived";
  publishedAt?: string;
  updatedAt?: string;
  readingMinutes?: number;
  featured?: boolean;
  /** The first topic is the main topic, shown above the title. */
  topicIds?: string[];
  category?: string;
  tags?: string[];
  cover?: ArticleImage;
  thumbnail?: ArticleImage;
  authorIds?: string[];
  body: ArticleBlock[];
  seo?: {
    title?: string;
    description?: string;
    image?: ArticleImage;
    detailsPageSlug?: string;
  };
}

export interface BlogData {
  version: 1;
  nextPublicationAt?: string;
  /** Preferences only the CMS reads. */
  cms?: Record<string, unknown>;
  blogs: Blog[];
  topics?: BlogTopic[];
  authors: BlogAuthor[];
  posts: Article[];
}

export interface ArticleContext {
  preview?: boolean;
  article: Article;
  detailsPageSlug: string;
  canonicalPath: string;
  /** Set for an article of an artikelside with a `path`, rendered through its template page. */
  newStyle?: boolean;
  /** New-style only: where the header's back link returns to by default. */
  backPath?: string;
}

/** Which overview an artikelside's address asked for: `{path}`, `/side/n`, `/emne/x` or `/forfatter/y`. */
export interface ArticleOverviewContext {
  blogId: string;
  topicId?: string;
  authorId?: string;
  /** 1 on the overview itself. */
  pageNumber: number;
  /** `?soeg=` on the overview. */
  search?: string;
}

export const articleVariations = {
  intro: ["editorial", "compact", "banner"],
  featured: ["split", "cover", "magazine"],
  collection: ["grid", "list", "magazine", "masonry"],
  home: ["cards", "feature-list", "carousel"],
  card: ["classic", "horizontal", "cover"],
  discovery: ["tabs", "chips", "dropdown"],
  pagination: ["numbers", "load-more", "infinite"],
  "post-header": ["centered", "split", "cover"],
  metadata: ["inline", "author", "minimal"],
  body: ["classic", "toc", "magazine", "sections"],
  media: ["wide", "gallery", "video"],
  author: ["inline", "bio", "contact"],
  related: ["cards", "list", "carousel"],
  cta: ["contact", "newsletter", "photo"],
  states: ["no-image", "empty", "no-results", "loading", "error"],
} as const;

export type ArticleFamily = keyof typeof articleVariations;
export interface ArticleComponentConfig {
  family: ArticleFamily;
  variant?: string;
  blogId?: string;
  detailsPageSlug?: string;
  /** Discovery, collection and pagination with the same key share filter state. */
  listingKey?: string;
  title?: string;
  eyebrow?: string;
  text?: string;
  articleIds?: string[];
  articleId?: string;
  category?: string;
  featuredOnly?: boolean;
  limit?: number;
  pageSize?: number;
  columns?: { mobile?: number; desktop?: number };
  cardVariant?: "classic" | "horizontal" | "cover";
  pagination?: "numbers" | "load-more" | "infinite" | "none";
  showPagination?: boolean;
  showExcerpt?: boolean;
  showMetadata?: boolean;
  /** Cover post-header minimum height; `auto` removes the minimum. */
  headerHeight?: string;
  headerVerticalAlign?: "start" | "center" | "end";
  headerCopyPadding?: string;
  headerCopyWidth?: string;
  headerTitleStyle?: ArticleBlockTypography;
  showBackLink?: boolean;
  /** Overrides the body's padding, optionally `mobile | desktop`. */
  bodyPadding?: string;
  /** Optional independent metadata controls for focused article lists. */
  showDate?: boolean;
  showAuthor?: boolean;
  link?: string;
  linkLabel?: string;
  dateLocale?: string;
  readingLabel?: string;
  linkIcon?: "chevron";
  metadataPlacement?: "before-excerpt" | "after-excerpt";
  image?: ArticleImage;
  images?: ArticleImage[];
  /** Where the list's pictures are placed when cropped; an article's own focus point wins. */
  imagePosition?: string;
  videoUrl?: string;
  /** Newsletter uses the configured CMS contact form, never an arbitrary external endpoint. */
  newsletterForm?: boolean;
  /** An Artikler list of a new-style artikelside: the server already chose `articleIds`, never the current article. */
  newStyle?: boolean;
}
